import React, { useState } from "react"
import { categoryLabel, clubLabel, competitionsLabel, instantLabel, playModeLabel, shortDateLabel } from "./plannerLabels"
import { fieldLabel, historyLine } from "./proposalHistory"
import { instantDaysLabel } from "./sendingLabels"
import WarningRow from "./WarningRow"

// The drawer's record tabs: the organizer's answer (and recording it, Phase C3), the warnings and the history.

const SENT_HOW = { outsidePlatform: "sent outside the platform", email: "by email from the platform" }
// The answers a proposal takes (C3 pinned decision Q5; Phase C2a): a Ciornă is confirmed directly (through Propusă), a Propusă confirmed
// or declined, a Confirmată declined.
const TAKES = { draft: ["confirmed"], proposed: ["confirmed", "refused"], confirmed: ["refused"] }
const ANSWER_LABELS = { confirmed: "Confirmed", refused: "Declined" }
const answeredBy = actor => (actor?.channel === "os" ? `, in SportyaOS by ${actor.name || "the organizer"}` : actor?.name ? `, recorded by ${actor.name}` : "")

export function AnswerTab({ proposal, row, settings, timeZone, onRecord }) {
  const sent = proposal.sentVersion
  const lines = []
  if (proposal.state === "draft") lines.push(proposal.everProposed ? "Reopened as a draft; not sent again yet." : "Not sent to its organizer yet.")
  if (sent) lines.push(`Last sent ${instantLabel(sent.at, timeZone)}, ${SENT_HOW[sent.how] || sent.how}.`)
  if (proposal.state === "proposed") lines.push(row?.marks?.notSentYet ? "Not sent yet to its current organizer." : "Waiting for the organizer's answer.")
  if (proposal.answer) {
    lines.push(`${proposal.answer.decision === "confirmed" ? "Confirmed" : "Declined"} ${instantLabel(proposal.answer.at, timeZone)}`
      + answeredBy(proposal.answer.actor) + (proposal.answer.note ? `: “${proposal.answer.note}”` : "."))
  }
  if (row?.marks?.needsNewConfirmation) lines.push("Changed after the confirmation: it needs a new confirmation.")
  const changed = row?.marks?.changedAfterSent || []
  const request = proposal.changeRequest
  return <div className="ep-stack ep-gap">
    {lines.map(line => <p key={line} className="mb-0">{line}</p>)}
    {request && <p className="ep-request mb-0">{`Change asked in SportyaOS by ${request.actor?.name || "the organizer"} on ${shortDateLabel(request.at, timeZone)}: `
      + `„${request.message}”`}</p>}
    {changed.length > 0 && <p className="ep-banner ep-banner-warn mb-0">Changed after it was sent: {changed.map(({ field }) => fieldLabel(field).toLowerCase()).join(", ")}.</p>}
    {TAKES[proposal.state] && onRecord && <RecordAnswer key={`${proposal.id}-${proposal.revision}`} proposal={proposal} timeZone={timeZone}
      changed={changed.length > 0} onRecord={onRecord} />}
    {sent?.content && <div className="ep-stack">
      <span className="ep-label">What the organizer was sent</span>
      <dl className="ep-facts">
        <dt>Dates</dt><dd>{instantDaysLabel(sent.content, timeZone)}</dd>
        <dt>Club</dt><dd>{clubLabel(sent.content.club)}</dd>
        <dt>Category</dt><dd>{categoryLabel(settings, sent.content.category)}</dd>
        <dt>Play mode</dt><dd>{playModeLabel(sent.content.playMode) || "—"}</dd>
        <dt>Competitions</dt><dd>{competitionsLabel(sent.content.competitions) || "—"} <span className="ep-meta">(informative)</span></dd>
      </dl>
    </div>}
    <p className="ep-meta mb-0">The organizer confirms the dates, club, organizer, category, play mode, sport and circuit. Competitions are informative:
      changing them keeps a confirmation. For a partial agreement, edit the proposal first, then record the confirmation on the new version.</p>
  </div>
}

// "Record answer": the organizer's answer given by phone, email or WhatsApp, bound to the revision shown, with an optional note and, with a
// confirmation, the organizer's new name (ticket 09). Sent after the 7 s Undo (C3 pinned decision Q1).
function RecordAnswer({ proposal, timeZone, changed, onRecord }) {
  const takes = TAKES[proposal.state]
  const [decision, setDecision] = useState(null)
  const [name, setName] = useState("")
  const [note, setNote] = useState("")
  const record = () => onRecord({ number: proposal.number, answers: [{ id: proposal.id, revision: proposal.revision, decision,
    ...(decision === "confirmed" && name.trim() && { name: name.trim() }) }], ...(note.trim() && { note: note.trim() }) })
  return <section className="ep-card ep-stack ep-gap ep-record" aria-label="Record answer">
    <span className="ep-label">Record the organizer's answer</span>
    <span role="radiogroup" aria-label={`Answer for ${proposal.number}`} className="ep-answer">
      {takes.map(answer => <label key={answer} className={"ep-answer-option" + (decision === answer ? " is-on" : "")}>
        <input type="radio" name={`record-${proposal.id}`} checked={decision === answer} onChange={() => setDecision(answer)} />{ANSWER_LABELS[answer]}</label>)}
    </span>
    {proposal.state === "draft" && <p className="ep-meta mb-0">A draft confirmed directly passes through Proposed without a sending: the organizer's next list
      shows it as confirmed. It must be one that could be sent.</p>}
    {decision === "confirmed" && changed && proposal.sentVersion && <p className="ep-banner ep-banner-warn mb-0">{`You confirm ${instantDaysLabel(proposal, timeZone)}`
      + `, not what was sent (${instantDaysLabel(proposal.sentVersion.content, timeZone)}).`}</p>}
    {decision === "confirmed" && <div className="ep-field">
      <label htmlFor="ep-record-name">New name (optional)</label>
      <input id="ep-record-name" className="form-control" maxLength={200} value={name} placeholder="If the organizer gave a new name"
        onChange={event => setName(event.target.value)} />
    </div>}
    <div className="ep-field">
      <label htmlFor="ep-record-note">Note (optional)</label>
      <input id="ep-record-note" className="form-control" maxLength={1000} value={note} placeholder="e.g. Answered on the phone on 21 Oct"
        onChange={event => setNote(event.target.value)} />
    </div>
    <p className="ep-meta mb-0">For a partial agreement, edit the proposal first, then record the confirmation of the new version.</p>
    <button type="button" className="btn btn-primary align-self-start" disabled={!decision} title={decision ? undefined : "Choose the organizer's answer first."}
      onClick={record}>Record answer</button>
  </section>
}

// The same rows as the quick peek (ticket 13 Q11), with Acknowledge… and Undo acknowledge.
export function WarningsTab({ warnings, timeZone, actions }) {
  if (!warnings.length) return <p className="ep-banner ep-banner-ok mb-0">No warnings for these dates and competitions.</p>
  return <div className="ep-stack ep-gap">
    {warnings.map(warning => <WarningRow key={warning.key} warning={warning} timeZone={timeZone} actions={actions} />)}
    <p className="ep-meta mb-0">Warnings never block. The rules and thresholds are under Useful information on the calendar.</p>
  </div>
}

export function HistoryTab({ history, context }) {
  return <ol className="ep-history">
    {[...history].reverse().map(entry => <li key={entry.id}>{historyLine(entry, context)}</li>)}
  </ol>
}
